import React, { useState, useEffect, useMemo } from 'react';
import { api, formatCurrency, formatDate } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
  Area,
  AreaChart
} from 'recharts';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Users,
  Package,
  Calendar,
  Download,
  FileSpreadsheet,
  FileText,
  Filter,
  ArrowUpRight,
  ArrowDownRight,
  Percent,
  DollarSign
} from 'lucide-react';
import { useStore } from '../context/StoreContext';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const COLORS = ['#6D28D9', '#84CC16', '#E11D48', '#F59E0B', '#3B82F6', '#10B981', '#EC4899'];

// Quick period presets
const PERIOD_PRESETS = [
  { label: 'Sot', value: 'today' },
  { label: 'Dje', value: 'yesterday' },
  { label: '7 Ditë', value: 'week' },
  { label: '30 Ditë', value: 'month' },
  { label: '90 Ditë', value: 'quarter' },
  { label: 'Ky Vit', value: 'year' },
];

export default function Reports() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [salesChart, setSalesChart] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [sales, setSales] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [activePeriod, setActivePeriod] = useState('month');
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);

  // Comparison period state
  const [showComparison, setShowComparison] = useState(false);
  const [comparisonSales, setComparisonSales] = useState([]);
  const [comparisonPurchases, setComparisonPurchases] = useState([]);

  // Store context
  const { currentStore, stores } = useStore();

  // Set dates based on period preset
  const setDatesByPeriod = (period) => {
    const today = new Date();
    let start = new Date();

    switch (period) {
      case 'today':
        start = today;
        break;
      case 'yesterday':
        start.setDate(today.getDate() - 1);
        today.setDate(today.getDate() - 1);
        break;
      case 'week':
        start.setDate(today.getDate() - 7);
        break;
      case 'month':
        start.setMonth(today.getMonth() - 1);
        break;
      case 'quarter':
        start.setMonth(today.getMonth() - 3);
        break;
      case 'year':
        start = new Date(today.getFullYear(), 0, 1);
        break;
      default:
        start.setMonth(today.getMonth() - 1);
    }

    setActivePeriod(period);
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(period === 'yesterday' ? start.toISOString().split('T')[0] : new Date().toISOString().split('T')[0]);
  };

  useEffect(() => {
    loadData();
  }, [startDate, endDate, currentStore]);

  const loadData = async () => {
    try {
      setLoading(true);
      const storeParam = currentStore?.id ? `store_id=${currentStore.id}` : '';
      const dateParams = `start_date=${startDate}&end_date=${endDate}`;
      const separator = storeParam ? '&' : '';

      const [statsRes, chartRes, topRes, salesRes, purchasesRes, customersRes, suppliersRes, categoriesRes, productsRes] = await Promise.all([
        api.get(`/dashboard/stats${storeParam ? '?' + storeParam : ''}`),
        api.get(`/dashboard/sales-chart?days=30${storeParam ? '&' + storeParam : ''}`),
        api.get(`/dashboard/top-products?limit=10${storeParam ? '&' + storeParam : ''}`),
        api.get(`/sales?${dateParams}${separator}${storeParam}`),
        api.get(`/purchases?${dateParams}${separator}${storeParam}`),
        api.get(`/customers${storeParam ? '?' + storeParam : ''}`),
        api.get(`/suppliers${storeParam ? '?' + storeParam : ''}`),
        api.get(`/categories${storeParam ? '?' + storeParam : ''}`),
        api.get(`/products${storeParam ? '?' + storeParam : ''}`)
      ]);
      setStats(statsRes.data);
      setSalesChart(chartRes.data);
      setTopProducts(topRes.data);
      setSales(salesRes.data);
      setPurchases(purchasesRes.data);
      setCustomers(customersRes.data);
      setSuppliers(suppliersRes.data);
      setCategories(categoriesRes.data);
      setProducts(productsRes.data);

      // Load comparison data (previous period)
      if (showComparison) {
        await loadComparisonData();
      }
    } catch (error) {
      console.error('Error loading reports:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load comparison period data
  const loadComparisonData = async () => {
    try {
      const periodDays = Math.ceil((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24));
      const compEnd = new Date(startDate);
      compEnd.setDate(compEnd.getDate() - 1);
      const compStart = new Date(compEnd);
      compStart.setDate(compStart.getDate() - periodDays);

      const storeParam = currentStore?.id ? `store_id=${currentStore.id}` : '';
      const dateParams = `start_date=${compStart.toISOString().split('T')[0]}&end_date=${compEnd.toISOString().split('T')[0]}`;
      const separator = storeParam ? '&' : '';

      const [salesRes, purchasesRes] = await Promise.all([
        api.get(`/sales?${dateParams}${separator}${storeParam}`),
        api.get(`/purchases?${dateParams}${separator}${storeParam}`)
      ]);

      setComparisonSales(salesRes.data);
      setComparisonPurchases(purchasesRes.data);
    } catch (error) {
      console.error('Error loading comparison data:', error);
    }
  };

  const [paymentMethod, setPaymentMethod] = useState('all');

  // Filter sales by category and payment method
  const filteredSales = useMemo(() => {
    let result = sales;

    // Filter by payment method
    if (paymentMethod !== 'all') {
      result = result.filter(sale => sale.payment_method === paymentMethod);
    }

    // Filter by category
    if (selectedCategory !== 'all') {
      result = result.filter(sale => {
        return sale.items?.some(item => {
          const product = products.find(p => p.id === item.product_id);
          return product?.category_id === selectedCategory;
        });
      });
    }

    return result;
  }, [sales, selectedCategory, paymentMethod, products]);

  // Calculate filtered totals
  const filteredTotalSales = useMemo(() => {
    if (selectedCategory === 'all') {
      return filteredSales.reduce((sum, s) => sum + s.total, 0);
    }

    let total = 0;
    filteredSales.forEach(sale => {
      sale.items?.forEach(item => {
        const product = products.find(p => p.id === item.product_id);
        if (product?.category_id === selectedCategory) {
          total += item.total;
        }
      });
    });
    return total;
  }, [filteredSales, selectedCategory, products]);

  // Category-wise sales breakdown
  const categorySales = useMemo(() => {
    const breakdown = {};

    sales.forEach(sale => {
      sale.items?.forEach(item => {
        const product = products.find(p => p.id === item.product_id);
        const category = categories.find(c => c.id === product?.category_id);
        const categoryName = category?.name || 'Pa kategori';

        if (!breakdown[categoryName]) {
          breakdown[categoryName] = { name: categoryName, total: 0, quantity: 0 };
        }
        breakdown[categoryName].total += item.total;
        breakdown[categoryName].quantity += item.quantity;
      });
    });

    return Object.values(breakdown).sort((a, b) => b.total - a.total);
  }, [sales, products, categories]);

  // Payment method breakdown
  const paymentMethodSales = useMemo(() => {
    const breakdown = {
      cash: { name: 'Cash', value: 0, color: '#10B981' },
      card: { name: 'Kartë / Bankë', value: 0, color: '#3B82F6' }
    };

    sales.forEach(s => {
      const method = s.payment_method === 'cash' ? 'cash' : 'card';
      breakdown[method].value += s.total;
    });

    return [breakdown.cash, breakdown.card].filter(x => x.value > 0);
  }, [sales]);

  // Export to Excel
  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();

    // Sales sheet
    const salesData = filteredSales.map(s => ({
      'Data': formatDate(s.created_at),
      'Klienti': s.customer_name,
      'Totali': s.total,
      'Paguar': s.paid,
      'Borxh': s.debt,
      'Metoda': s.payment_method === 'cash' ? 'Cash' : 'Kartë'
    }));
    const salesSheet = XLSX.utils.json_to_sheet(salesData);
    XLSX.utils.book_append_sheet(wb, salesSheet, 'Shitjet');

    // Products sheet
    const productsData = topProducts.map(p => ({
      'Produkti': p.name,
      'Sasia': p.quantity,
      'Të ardhura': p.revenue
    }));
    const productsSheet = XLSX.utils.json_to_sheet(productsData);
    XLSX.utils.book_append_sheet(wb, productsSheet, 'Produktet');

    // Category breakdown sheet
    const categoryData = categorySales.map(c => ({
      'Kategoria': c.name,
      'Sasia': c.quantity,
      'Totali': c.total
    }));
    const categorySheet = XLSX.utils.json_to_sheet(categoryData);
    XLSX.utils.book_append_sheet(wb, categorySheet, 'Kategoritë');

    // Debts sheet
    const debtsData = customers.filter(c => c.debt > 0).map(c => ({
      'Klienti': c.name,
      'Telefoni': c.phone,
      'Borxhi': c.debt
    }));
    const debtsSheet = XLSX.utils.json_to_sheet(debtsData);
    XLSX.utils.book_append_sheet(wb, debtsSheet, 'Borxhet');

    XLSX.writeFile(wb, `raport_${startDate}_${endDate}.xlsx`);
  };

  // Export to PDF
  const exportToPDF = () => {
    const doc = new jsPDF();
    const storeName = currentStore?.name || 'Të gjithë dyqanet';

    // Header
    doc.setFontSize(18);
    doc.text('Raport i Shitjeve', 14, 22);
    doc.setFontSize(11);
    doc.text(`${storeName} | ${startDate} - ${endDate}`, 14, 30);

    // Summary
    doc.setFontSize(14);
    doc.text('Përmbledhje', 14, 45);
    doc.setFontSize(10);
    doc.text(`Shitje Total: ${formatCurrency(totalSales)}`, 14, 55);
    doc.text(`Blerje Total: ${formatCurrency(totalPurchases)}`, 14, 62);
    doc.text(`Fitimi: ${formatCurrency(profit)}`, 14, 69);
    doc.text(`Borxhe Klientësh: ${formatCurrency(customers.reduce((sum, c) => sum + c.debt, 0))}`, 14, 76);

    // Sales table
    doc.setFontSize(14);
    doc.text('Shitjet e Fundit', 14, 90);

    doc.autoTable({
      startY: 95,
      head: [['Data', 'Klienti', 'Total', 'Paguar']],
      body: filteredSales.slice(0, 20).map(s => [
        formatDate(s.created_at),
        s.customer_name,
        formatCurrency(s.total),
        formatCurrency(s.paid)
      ]),
      theme: 'striped',
      headStyles: { fillColor: [109, 40, 217] }
    });

    // Top products
    const finalY = doc.lastAutoTable.finalY + 15;
    doc.setFontSize(14);
    doc.text('Produktet Më të Shitura', 14, finalY);

    doc.autoTable({
      startY: finalY + 5,
      head: [['Produkti', 'Sasia', 'Të ardhura']],
      body: topProducts.slice(0, 10).map(p => [
        p.name,
        p.quantity,
        formatCurrency(p.revenue)
      ]),
      theme: 'striped',
      headStyles: { fillColor: [132, 204, 22] }
    });

    doc.save(`raport_${startDate}_${endDate}.pdf`);
  };

  const totalSales = filteredTotalSales;
  const totalPurchases = purchases.reduce((sum, p) => sum + p.total, 0);
  const profit = totalSales - totalPurchases;
  const customersWithDebt = customers.filter(c => c.debt > 0);
  const suppliersWithDebt = suppliers.filter(s => s.debt > 0);

  // Comparison calculations
  const compTotalSales = comparisonSales.reduce((sum, s) => sum + s.total, 0);
  const compTotalPurchases = comparisonPurchases.reduce((sum, p) => sum + p.total, 0);
  const salesChange = compTotalSales > 0 ? ((totalSales - compTotalSales) / compTotalSales * 100) : 0;
  const purchasesChange = compTotalPurchases > 0 ? ((totalPurchases - compTotalPurchases) / compTotalPurchases * 100) : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      {/* Filters Card */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-col gap-4">
            {/* Quick Period Buttons */}
            <div className="flex flex-wrap gap-2">
              {PERIOD_PRESETS.map((preset) => (
                <Button
                  key={preset.value}
                  variant={activePeriod === preset.value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setDatesByPeriod(preset.value)}
                  data-testid={`period-${preset.value}`}
                >
                  {preset.label}
                </Button>
              ))}
            </div>

            {/* Date and Category Filters */}
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <Label className="text-xs text-slate-500">Nga data</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setActivePeriod('custom');
                  }}
                  className="w-40"
                  data-testid="start-date-input"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Deri në datën</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setActivePeriod('custom');
                  }}
                  className="w-40"
                  data-testid="end-date-input"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Kategoria</Label>
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="w-48" data-testid="category-filter">
                    <SelectValue placeholder="Të gjitha kategorit" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Të gjitha kategorit</SelectItem>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Mënyra e Pagesës</Label>
                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                  <SelectTrigger className="w-40" data-testid="payment-method-filter">
                    <SelectValue placeholder="Të gjitha" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Të gjitha</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="card">Kartë / Bankë</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" onClick={loadData} data-testid="apply-filters-btn">
                <Filter className="w-4 h-4 mr-2" />
                Apliko
              </Button>
              <Button
                variant={showComparison ? "default" : "outline"}
                onClick={() => {
                  setShowComparison(!showComparison);
                  if (!showComparison) loadComparisonData();
                }}
                data-testid="compare-btn"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                {showComparison ? 'Fshih Krahasimin' : 'Krahaso'}
              </Button>
              <div className="flex-1" />
              {/* Export Buttons */}
              <Button variant="outline" onClick={exportToExcel} data-testid="export-excel-btn">
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Excel
              </Button>
              <Button variant="outline" onClick={exportToPDF} data-testid="export-pdf-btn">
                <FileText className="w-4 h-4 mr-2" />
                PDF
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="overflow-hidden">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Shitje Total</p>
                <p className="text-2xl font-bold font-mono text-primary">{formatCurrency(totalSales)}</p>
                <p className="text-xs text-slate-400">{filteredSales.length} transaksione</p>
                {showComparison && compTotalSales > 0 && (
                  <div className={`flex items-center gap-1 text-xs mt-1 ${salesChange >= 0 ? 'text-lime-600' : 'text-rose-600'}`}>
                    {salesChange >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                    <span>{Math.abs(salesChange).toFixed(1)}% vs periudha e kaluar</span>
                  </div>
                )}
              </div>
              <div className="p-3 bg-primary/10 rounded-full">
                <TrendingUp className="w-6 h-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="overflow-hidden">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Blerje Total</p>
                <p className="text-2xl font-bold font-mono text-orange-600">{formatCurrency(totalPurchases)}</p>
                <p className="text-xs text-slate-400">{purchases.length} transaksione</p>
                {showComparison && compTotalPurchases > 0 && (
                  <div className={`flex items-center gap-1 text-xs mt-1 ${purchasesChange <= 0 ? 'text-lime-600' : 'text-rose-600'}`}>
                    {purchasesChange >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                    <span>{Math.abs(purchasesChange).toFixed(1)}% vs periudha e kaluar</span>
                  </div>
                )}
              </div>
              <div className="p-3 bg-orange-100 rounded-full">
                <Package className="w-6 h-6 text-orange-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="overflow-hidden">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Fitimi</p>
                <p className={`text-2xl font-bold font-mono ${profit >= 0 ? 'text-lime-600' : 'text-rose-600'}`}>
                  {formatCurrency(profit)}
                </p>
                <p className="text-xs text-slate-400">
                  Marzhi: {totalSales > 0 ? ((profit / totalSales) * 100).toFixed(1) : 0}%
                </p>
              </div>
              <div className={`p-3 rounded-full ${profit >= 0 ? 'bg-lime-100' : 'bg-rose-100'}`}>
                <BarChart3 className={`w-6 h-6 ${profit >= 0 ? 'text-lime-600' : 'text-rose-600'}`} />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="overflow-hidden">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Borxhe Klientësh</p>
                <p className="text-2xl font-bold font-mono text-rose-600">
                  {formatCurrency(customers.reduce((sum, c) => sum + c.debt, 0))}
                </p>
                <p className="text-xs text-slate-400">{customersWithDebt.length} klientë me borxh</p>
              </div>
              <div className="p-3 bg-rose-100 rounded-full">
                <Users className="w-6 h-6 text-rose-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Comparison Section - Shows when comparison is enabled */}
      {showComparison && (
        <Card className="border-2 border-primary/20 bg-gradient-to-r from-primary/5 to-transparent">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Krahasimi me Periudhën e Kaluar
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Current Period */}
              <div className="space-y-2">
                <p className="text-xs text-slate-500 uppercase font-semibold">Periudha Aktuale</p>
                <p className="text-sm text-slate-600">{formatDate(startDate)} - {formatDate(endDate)}</p>
                <div className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Shitje:</span>
                    <span className="font-mono font-bold text-primary">{formatCurrency(totalSales)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Blerje:</span>
                    <span className="font-mono font-bold text-orange-600">{formatCurrency(totalPurchases)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Fitimi:</span>
                    <span className={`font-mono font-bold ${profit >= 0 ? 'text-lime-600' : 'text-rose-600'}`}>
                      {formatCurrency(profit)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Previous Period */}
              <div className="space-y-2">
                <p className="text-xs text-slate-500 uppercase font-semibold">Periudha e Kaluar</p>
                <p className="text-sm text-slate-600">
                  {(() => {
                    const periodDays = Math.ceil((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24));
                    const compEnd = new Date(startDate);
                    compEnd.setDate(compEnd.getDate() - 1);
                    const compStart = new Date(compEnd);
                    compStart.setDate(compStart.getDate() - periodDays);
                    return `${formatDate(compStart.toISOString())} - ${formatDate(compEnd.toISOString())}`;
                  })()}
                </p>
                <div className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Shitje:</span>
                    <span className="font-mono text-slate-600">{formatCurrency(compTotalSales)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Blerje:</span>
                    <span className="font-mono text-slate-600">{formatCurrency(compTotalPurchases)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Fitimi:</span>
                    <span className="font-mono text-slate-600">
                      {formatCurrency(compTotalSales - compTotalPurchases)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Change Indicators */}
              <div className="space-y-2">
                <p className="text-xs text-slate-500 uppercase font-semibold">Ndryshimi</p>
                <div className="space-y-3 pt-6">
                  <div className="flex items-center gap-2">
                    {salesChange >= 0 ? (
                      <div className="p-1 rounded-full bg-lime-100">
                        <ArrowUpRight className="w-4 h-4 text-lime-600" />
                      </div>
                    ) : (
                      <div className="p-1 rounded-full bg-rose-100">
                        <ArrowDownRight className="w-4 h-4 text-rose-600" />
                      </div>
                    )}
                    <span className={`font-bold ${salesChange >= 0 ? 'text-lime-600' : 'text-rose-600'}`}>
                      {salesChange >= 0 ? '+' : ''}{salesChange.toFixed(1)}% Shitje
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {purchasesChange <= 0 ? (
                      <div className="p-1 rounded-full bg-lime-100">
                        <ArrowDownRight className="w-4 h-4 text-lime-600" />
                      </div>
                    ) : (
                      <div className="p-1 rounded-full bg-rose-100">
                        <ArrowUpRight className="w-4 h-4 text-rose-600" />
                      </div>
                    )}
                    <span className={`font-bold ${purchasesChange <= 0 ? 'text-lime-600' : 'text-rose-600'}`}>
                      {purchasesChange >= 0 ? '+' : ''}{purchasesChange.toFixed(1)}% Blerje
                    </span>
                  </div>
                </div>
              </div>

              {/* Comparison Chart */}
              <div>
                <p className="text-xs text-slate-500 uppercase font-semibold mb-2">Grafik</p>
                <div className="h-28">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={[
                      { name: 'E kaluar', shitje: compTotalSales, blerje: compTotalPurchases },
                      { name: 'Aktuale', shitje: totalSales, blerje: totalPurchases }
                    ]}>
                      <XAxis dataKey="name" fontSize={10} />
                      <YAxis fontSize={10} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Bar dataKey="shitje" fill="#6D28D9" radius={[4, 4, 0, 0]} name="Shitje" />
                      <Bar dataKey="blerje" fill="#F59E0B" radius={[4, 4, 0, 0]} name="Blerje" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="sales" className="space-y-4">
        <TabsList>
          <TabsTrigger value="sales" data-testid="tab-sales">Shitjet</TabsTrigger>
          <TabsTrigger value="products" data-testid="tab-products">Produktet</TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-categories">Kategorit</TabsTrigger>
          <TabsTrigger value="debts" data-testid="tab-debts">Borxhet</TabsTrigger>
        </TabsList>

        {/* Sales Tab */}
        <TabsContent value="sales" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Sales Chart */}
            <Card className="col-span-1 lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg">Shitjet 30 Ditët e Fundit</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={salesChart}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis
                        dataKey="date"
                        stroke="#64748b"
                        fontSize={12}
                        tickFormatter={(value) => {
                          const date = new Date(value);
                          return date.toLocaleDateString('sq-AL', { day: 'numeric' });
                        }}
                      />
                      <YAxis
                        stroke="#64748b"
                        fontSize={12}
                        tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
                      />
                      <Tooltip
                        formatter={(value) => [formatCurrency(value), 'Shitje']}
                        labelFormatter={(label) => formatDate(label)}
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px',
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="total"
                        stroke="#6D28D9"
                        strokeWidth={3}
                        dot={{ fill: '#6D28D9', strokeWidth: 2 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Payment Method Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Mënyra e Pagesës</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={paymentMethodSales}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {paymentMethodSales.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Recent Sales */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Shitjet e Fundit</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="max-h-64 overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Data</TableHead>
                        <TableHead>Klienti</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredSales.slice(0, 10).map((sale) => (
                        <TableRow key={sale.id}>
                          <TableCell className="font-mono text-xs">{formatDate(sale.created_at)}</TableCell>
                          <TableCell>{sale.customer_name}</TableCell>
                          <TableCell className="text-right font-mono">{formatCurrency(sale.total)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Products Tab */}
        <TabsContent value="products" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Products Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Produktet Më të Shitura</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={topProducts} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis
                        type="number"
                        stroke="#64748b"
                        fontSize={12}
                        tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
                      />
                      <YAxis
                        dataKey="name"
                        type="category"
                        stroke="#64748b"
                        fontSize={12}
                        width={80}
                        tickFormatter={(value) => value.length > 12 ? value.slice(0, 12) + '...' : value}
                      />
                      <Tooltip
                        formatter={(value) => [formatCurrency(value), 'Të ardhura']}
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px',
                        }}
                      />
                      <Bar dataKey="revenue" fill="#84CC16" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Products Table */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Detajet e Produkteve</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="max-h-64 overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Produkti</TableHead>
                        <TableHead className="text-right">Sasia</TableHead>
                        <TableHead className="text-right">Të ardhura</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {topProducts.map((product, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-medium">{product.name}</TableCell>
                          <TableCell className="text-right font-mono">{product.quantity}</TableCell>
                          <TableCell className="text-right font-mono">{formatCurrency(product.revenue)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Category Pie Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Shitjet sipas Kategorisë</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={categorySales.slice(0, 7)}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        dataKey="total"
                        nameKey="name"
                        label={({ name, percent }) => `${name.substring(0, 10)}${name.length > 10 ? '...' : ''} (${(percent * 100).toFixed(0)}%)`}
                        labelLine={false}
                      >
                        {categorySales.slice(0, 7).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value) => [formatCurrency(value), 'Shitje']}
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px',
                        }}
                      />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Category Table */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Detajet e Kategorive</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="max-h-72 overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Kategoria</TableHead>
                        <TableHead className="text-right">Sasia</TableHead>
                        <TableHead className="text-right">Të ardhura</TableHead>
                        <TableHead className="text-right">%</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {categorySales.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} className="text-center py-8 text-slate-500">
                            Nuk ka të dhëna për kategorit
                          </TableCell>
                        </TableRow>
                      ) : (
                        categorySales.map((cat, index) => (
                          <TableRow key={index}>
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                <div
                                  className="w-3 h-3 rounded-full"
                                  style={{ backgroundColor: COLORS[index % COLORS.length] }}
                                />
                                {cat.name}
                              </div>
                            </TableCell>
                            <TableCell className="text-right font-mono">{cat.quantity}</TableCell>
                            <TableCell className="text-right font-mono">{formatCurrency(cat.total)}</TableCell>
                            <TableCell className="text-right font-mono text-slate-500">
                              {totalSales > 0 ? ((cat.total / totalSales) * 100).toFixed(1) : 0}%
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
                {categorySales.length > 0 && (
                  <div className="p-4 border-t bg-slate-50">
                    <div className="flex justify-between font-bold">
                      <span>Total:</span>
                      <span className="font-mono text-primary">
                        {formatCurrency(categorySales.reduce((sum, c) => sum + c.total, 0))}
                      </span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Category Trend Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Krahasimi i Kategorive</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={categorySales.slice(0, 6)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis
                      type="number"
                      stroke="#64748b"
                      fontSize={12}
                      tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
                    />
                    <YAxis
                      dataKey="name"
                      type="category"
                      stroke="#64748b"
                      fontSize={12}
                      width={100}
                      tickFormatter={(value) => value.length > 15 ? value.slice(0, 15) + '...' : value}
                    />
                    <Tooltip
                      formatter={(value, name) => [formatCurrency(value), name === 'total' ? 'Të ardhura' : 'Sasia']}
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                      }}
                    />
                    <Bar dataKey="total" fill="#6D28D9" radius={[0, 4, 4, 0]} name="Të ardhura" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Debts Tab */}
        <TabsContent value="debts" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Customer Debts */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg text-rose-600">Borxhet e Klientëve</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="max-h-80 overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Klienti</TableHead>
                        <TableHead>Telefoni</TableHead>
                        <TableHead className="text-right">Borxhi</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {customersWithDebt.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={3} className="text-center py-8 text-slate-500">
                            Nuk ka borxhe nga klientët
                          </TableCell>
                        </TableRow>
                      ) : (
                        customersWithDebt.map((customer) => (
                          <TableRow key={customer.id}>
                            <TableCell className="font-medium">{customer.name}</TableCell>
                            <TableCell className="text-sm">{customer.phone || '-'}</TableCell>
                            <TableCell className="text-right font-mono text-rose-600 font-bold">
                              {formatCurrency(customer.debt)}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
                {customersWithDebt.length > 0 && (
                  <div className="p-4 border-t bg-rose-50">
                    <div className="flex justify-between font-bold">
                      <span>Total:</span>
                      <span className="font-mono text-rose-600">
                        {formatCurrency(customersWithDebt.reduce((sum, c) => sum + c.debt, 0))}
                      </span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Supplier Debts */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg text-orange-600">Borxhet ndaj Furnitorëve</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="max-h-80 overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Furnitori</TableHead>
                        <TableHead>Telefoni</TableHead>
                        <TableHead className="text-right">Borxhi</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {suppliersWithDebt.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={3} className="text-center py-8 text-slate-500">
                            Nuk ka borxhe ndaj furnitorëve
                          </TableCell>
                        </TableRow>
                      ) : (
                        suppliersWithDebt.map((supplier) => (
                          <TableRow key={supplier.id}>
                            <TableCell className="font-medium">{supplier.name}</TableCell>
                            <TableCell className="text-sm">{supplier.phone || '-'}</TableCell>
                            <TableCell className="text-right font-mono text-orange-600 font-bold">
                              {formatCurrency(supplier.debt)}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
                {suppliersWithDebt.length > 0 && (
                  <div className="p-4 border-t bg-orange-50">
                    <div className="flex justify-between font-bold">
                      <span>Total:</span>
                      <span className="font-mono text-orange-600">
                        {formatCurrency(suppliersWithDebt.reduce((sum, s) => sum + s.debt, 0))}
                      </span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
